/**
 * Page 5: Your 12-Week Timeline (Minimal MVP version)
 *
 * Final page with "Start My First Day with BOSI" button that completes onboarding.
 * Feature: 017-body-insight-summary
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BodyInsightProgress } from '../../components/body-insight/BodyInsightProgress';
import { getPageContent } from '../../services/bodyInsightTemplates';
import { apiClient } from '../../services/api';

export const Page5TwelveWeekTimeline: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get personalized content for this page
  const content = user ? getPageContent(5, user.profile as any) : null;

  const handleStartFirstDay = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Call the completion endpoint using apiClient (handles auth automatically)
      const response = await apiClient.patch('/onboarding/complete');
      const data = response.data;

      // Update user context with new onboarding status
      if (data.user) {
        updateUser(data.user);
      }

      // Navigate to first check-in
      navigate('/checkin');
    } catch (err: any) {
      console.error('Error completing onboarding:', err);
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/body-insight/4');
  };

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF]">
        <p className="text-gray-600">Unable to load personalized content.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF] py-8 px-4">
      <div className="max-w-[800px] mx-auto">
        {/* Progress indicator */}
        <BodyInsightProgress currentPage={5} totalPages={5} />

        {/* Main content card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Headline */}
          <p className="text-sm uppercase tracking-wide text-[#B7A76D] mb-2 font-semibold">
            Here's what we understand about you
          </p>

          {/* Page title */}
          <h1 className="font-['Nunito'] text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            YOUR 12-WEEK TIMELINE 📅
          </h1>

          {/* Body content */}
          <div className="space-y-6">
            {/* Main headline */}
            {content.headline && (
              <h2 className="font-['Nunito'] text-2xl font-semibold text-[#C98CCC] mb-4">
                {content.headline}
              </h2>
            )}

            {/* Body text */}
            {content.body && (
              <p className="font-['Inter'] text-lg leading-relaxed text-gray-700 mb-6">
                {content.body}
              </p>
            )}

            {/* Timeline phases */}
            {content.timelinePhases && content.timelinePhases.length > 0 && (
              <div className="space-y-6 mb-8">
                {content.timelinePhases.map((phase, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-[#B7A76D] pl-6 py-4"
                  >
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-sm font-semibold text-[#B7A76D] uppercase">
                        {phase.weeks}
                      </span>
                      <h3 className="font-['Nunito'] text-xl font-bold text-gray-900">
                        {phase.title}
                      </h3>
                    </div>
                    <p className="font-['Inter'] text-base text-gray-700 mb-3">
                      <span className="font-semibold">Focus:</span> {phase.focus}
                    </p>
                    {phase.what_to_expect && phase.what_to_expect.length > 0 && (
                      <div>
                        <p className="font-['Inter'] text-sm font-semibold text-gray-600 mb-2">
                          What to expect:
                        </p>
                        <ul className="space-y-1">
                          {phase.what_to_expect.map((item, i) => (
                            <li
                              key={i}
                              className="flex items-start font-['Inter'] text-sm text-gray-600"
                            >
                              <span className="text-[#B7A76D] mr-2">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* One Thing to Remember Box */}
        <div className="bg-[#FFF5E9] border-2 border-[#C98CCC] rounded-xl p-8 mb-6 text-center">
          <p className="font-['Nunito'] text-sm uppercase tracking-wide text-[#B7A76D] mb-3 font-semibold">
            💬 ONE THING TO REMEMBER
          </p>
          <p className="font-['Nunito'] text-xl font-bold text-gray-900 mb-2">
            You're not stuck.
          </p>
          <p className="font-['Inter'] text-base text-gray-700">
            Your body just needs a different way now.
          </p>
        </div>

        {/* You're Not Doing This Alone Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <p className="font-['Nunito'] text-sm uppercase tracking-wide text-[#B7A76D] mb-3 font-semibold">
              🤝 YOU'RE NOT DOING THIS ALONE
            </p>
            <h2 className="font-['Nunito'] text-2xl font-bold text-gray-900 mb-4">
              BOSI will guide you daily based on your body signals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="p-4">
              <div className="text-4xl mb-3">💪</div>
              <p className="font-['Nunito'] text-lg font-semibold text-gray-900 mb-2">
                Some days you'll push
              </p>
              <p className="font-['Inter'] text-sm text-gray-600">
                When your body signals high energy and recovery
              </p>
            </div>

            <div className="p-4">
              <div className="text-4xl mb-3">🛌</div>
              <p className="font-['Nunito'] text-lg font-semibold text-gray-900 mb-2">
                Some days you'll recover
              </p>
              <p className="font-['Inter'] text-sm text-gray-600">
                When your body needs rest and gentle movement
              </p>
            </div>

            <div className="p-4">
              <div className="text-4xl mb-3">📈</div>
              <p className="font-['Nunito'] text-lg font-semibold text-gray-900 mb-2">
                And that's how progress happens
              </p>
              <p className="font-['Inter'] text-sm text-gray-600">
                Listening to your body, not fighting it
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#C98CCC]/10 to-[#B7A76D]/10 rounded-2xl p-8 mb-6 text-center">
          <h2 className="font-['Nunito'] text-2xl font-bold text-gray-900 mb-3">
            Ready to Begin?
          </h2>
          <p className="font-['Inter'] text-base text-gray-700 mb-6">
            Today's guidance will be based on your body signals
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={handleStartFirstDay}
            disabled={isLoading}
            className="w-full md:max-w-[400px] h-12 bg-gradient-to-r from-[#C98CCC] to-[#B7A76D] text-white font-bold text-lg rounded-full hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Starting...</span>
              </>
            ) : (
              'Start My First Day with BOSI'
            )}
          </button>
        </div>

        {/* Medical disclaimer */}
        <div className="bg-white/50 rounded-lg p-4 mb-6 border border-gray-200">
          <p className="text-sm text-gray-600 text-center font-['Inter']">
            💡 This guidance is educational and does not replace medical advice
          </p>
        </div>

        {/* Back button */}
        <div className="flex justify-start">
          <button
            onClick={handleBack}
            disabled={isLoading}
            className="bg-white text-[#B7A76D] font-semibold py-3 px-8 rounded-full border-2 border-[#B7A76D] hover:bg-[#B7A76D] hover:text-white transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Page5TwelveWeekTimeline;
